import { useCallback, useEffect, useRef } from 'react';

const ERROR_AUDIO_URL = `${process.env.REACT_APP_PUBLIC_BUCKET_BASE_URL}/twilio-audio/twilio-error.mp3`;
const HANGUP_AUDIO_URL = `${process.env.REACT_APP_PUBLIC_BUCKET_BASE_URL}/twilio-audio/end-call-audio.wav`;
const RINGING_AUDIO_URL = `${process.env.REACT_APP_PUBLIC_BUCKET_BASE_URL}/twilio-audio/outbound-call-ringing.wav`;

const HANGUP_AUDIO_TIMEOUT = 2000;
const HANGUP_AND_RINGING_AUDIO_VOLUME = 0.5;

enum AudioOperation {
  Play = 'play',
  Pause = 'pause',
}

enum AudioType {
  Ringing = 'ringing',
  Error = 'error',
  Hangup = 'hangup',
}

const usePlayCallAudio = () => {
  const audioRefs = useRef({
    [AudioType.Ringing]: new Audio(RINGING_AUDIO_URL),
    [AudioType.Error]: new Audio(ERROR_AUDIO_URL),
    [AudioType.Hangup]: new Audio(HANGUP_AUDIO_URL),
  });

  const pauseAudio = useCallback((audio: HTMLAudioElement) => {
    audio.pause();
    audio.currentTime = 0;
  }, []);

  const playAudio = useCallback(
    (audio: HTMLAudioElement, timeout = 0, resolve: (value: void | PromiseLike<void>) => void) => {
      // Start playing the audio.
      audio.play();

      // If there is a timeout, pause the audio after the specified timeout,
      const timeoutId = timeout
        ? setTimeout(() => {
            pauseAudio(audio);
            resolve();
          }, timeout)
        : undefined;

      // Resolve the promise if the audio ends naturally,
      // clearing the timeout if it exists (eg. if a timeout was set longer than the audio duration).
      audio.onended = () => {
        clearTimeout(timeoutId);
        resolve();
      };
    },
    [pauseAudio]
  );

  // Safe audio operation that rejects the promise if the audio operation fails.
  const safeAudioOperation = useCallback(
    (audioType: AudioType, operation: AudioOperation, timeout = 0) => {
      return new Promise<void>((resolve, reject) => {
        try {
          const audio = audioRefs.current[audioType];
          if (operation === AudioOperation.Play && audio.paused) {
            playAudio(audio, timeout, resolve);
          } else if (!audio.paused) {
            pauseAudio(audio);
          }
        } catch (error) {
          console.warn(`Audio ${operation} operation failed:`, error);
          reject(error);
        }
      });
    },
    [playAudio, pauseAudio]
  );

  const playHangUpAudio = useCallback(
    () => safeAudioOperation(AudioType.Hangup, AudioOperation.Play, HANGUP_AUDIO_TIMEOUT),
    [safeAudioOperation]
  );

  const pauseHangUpAudio = useCallback(
    () => safeAudioOperation(AudioType.Hangup, AudioOperation.Pause),
    [safeAudioOperation]
  );

  const playErrorAudio = useCallback(
    () => safeAudioOperation(AudioType.Error, AudioOperation.Play),
    [safeAudioOperation]
  );

  const pauseErrorAudio = useCallback(
    () => safeAudioOperation(AudioType.Error, AudioOperation.Pause),
    [safeAudioOperation]
  );

  const playRingingAudio = useCallback(
    () => safeAudioOperation(AudioType.Ringing, AudioOperation.Play),
    [safeAudioOperation]
  );

  const pauseRingingAudio = useCallback(
    () => safeAudioOperation(AudioType.Ringing, AudioOperation.Pause),
    [safeAudioOperation]
  );

  useEffect(() => {
    // Reduce the volume of the hangup audio.
    audioRefs.current[AudioType.Hangup].volume = HANGUP_AND_RINGING_AUDIO_VOLUME;

    // Set the ringing audio to loop and reduce the volume.
    audioRefs.current[AudioType.Ringing].loop = true;
    audioRefs.current[AudioType.Ringing].volume = HANGUP_AND_RINGING_AUDIO_VOLUME;

    // Cleanup the audio operations when the component unmounts.
    return () => {
      pauseRingingAudio();
      pauseHangUpAudio();
      pauseErrorAudio();
    };
  }, []);

  return {
    playErrorAudio,
    pauseErrorAudio,
    playHangUpAudio,
    pauseHangUpAudio,
    playRingingAudio,
    pauseRingingAudio,
  };
};

export default usePlayCallAudio;
